'use client';

import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import styled, { css } from 'styled-components';
import { copyColor, copyFontFamily, copyFontSizeSmall, formInputBackgroundColor, formBorderColor, formBorderColorValid, formBorderColorInvalid, standardFocusStyleDeclarations } from '@nib-components/theme';
import { breakpoint, p, pr, py, pl, Hidden } from '@nib/layout';
import { ChevronDownSystemIcon } from '@nib/icons';
const disabledCursor = 'not-allowed';
const disabledOpacity = 0.5;
const validFormModes = ['light', 'white'];
const Relative = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
`;
export const SelectInput = styled.select`
  display: inline-block;
  ${p(2)};
  font-size: 1rem;
  font-family: ${copyFontFamily};
  color: var(--themeColorFg, ${copyColor});
  line-height: 1.5;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  margin: 0;
  border: none;
  border-radius: 0;
  box-shadow: inset 0 -2px 0 0 var(--inputColorBorder, ${formBorderColor});
  background-color: var(--inputColorBg, ${formInputBackgroundColor});
  cursor: pointer;

  &::-ms-expand {
    display: none;
  }

  ${breakpoint('mini')`
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;

    ${py(4)};
    ${pl(4)};
    /* Non-standard padding to accommodate validation icon */
    padding-inline-end: 3.5rem;
  `}

  ${_ref => {
  let {
    isCompact
  } = _ref;
  return isCompact && css`
      &&& {
        ${p(2)};
        ${pr({
    xs: 2,
    mini: 7
  })};
        font-size: ${copyFontSizeSmall};
      }
    `;
}};

  ${_ref2 => {
  let {
    disabled
  } = _ref2;
  return disabled && css`
      opacity: ${disabledOpacity};
      cursor: ${disabledCursor};
    `;
}}

  /* Validated and invalid */
  ${_ref3 => {
  let {
    valid,
    validated
  } = _ref3;
  return !valid && validated && css`
      box-shadow: inset 0 0 0 2px var(--themeColorBorderError, ${formBorderColorInvalid});
    `;
}}

  /* Validated and valid */
  ${_ref4 => {
  let {
    valid,
    validated
  } = _ref4;
  return valid && validated && css`
      box-shadow: inset 0 -2px 0 0 var(--themeColorBorderSuccess, ${formBorderColorValid});
    `;
}}

  &:focus-visible {
    ${standardFocusStyleDeclarations};
  }
`;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const ChevronDownIcon = styled(_ref5 => {
  let {
    isCompact,
    ...otherProps
  } = _ref5;
  return /*#__PURE__*/_jsx(ChevronDownSystemIcon, {
    ...otherProps
  });
})`
  --endOffset: 0.5rem;
  ${_ref6 => {
  let {
    isCompact
  } = _ref6;
  return !isCompact && css`
      ${breakpoint('mini')`
      --endOffset: 1rem;
    `}
    `;
}}
  position: absolute;
  inset-block-start: 50%;
  transform: translateY(-50%);
  inset-inline-end: var(--endOffset);
  pointer-events: none;
  ${_ref7 => {
  let {
    disabled
  } = _ref7;
  return disabled && css`
      opacity: ${disabledOpacity};
    `;
}}
`;
/**
 * Render a select input
 * @param   {object} props
 * @returns {ReactElement}
 */
const Select = props => {
  const {
    placeholder,
    options,
    disabled = false,
    valid = false,
    validated = false,
    isCompact = false,
    formMode,
    children,
    ...otherProps
  } = props;
  let optionsArray = options;
  if (options && !Array.isArray(options)) {
    optionsArray = Object.keys(options).map(key => ({
      value: key,
      label: options[key]
    }));
  }
  return /*#__PURE__*/_jsxs(Relative, {
    "data-mesh-component": "SELECT",
    children: [/*#__PURE__*/_jsxs(SelectInput, {
      disabled: disabled,
      valid: valid,
      validated: validated,
      formMode: formMode,
      isCompact: isCompact,
      ...otherProps,
      children: [placeholder && /*#__PURE__*/_jsx("option", {
        value: "",
        children: placeholder
      }), options ? optionsArray.map((element, index) => /*#__PURE__*/_jsx("option", {
        value: element.value,
        children: element.label
      }, index)) : children]
    }), /*#__PURE__*/_jsx(Hidden, {
      below: "mini",
      children: /*#__PURE__*/_jsx(ChevronDownIcon, {
        size: "sm",
        disabled: disabled,
        isCompact: isCompact
      })
    })]
  });
};
Select.displayName = 'Select';
Select.SelectInput = SelectInput;
export default Select;